import { useEffect, useRef, useState } from "react";
import "styles/lineItemReorder.css";

const DROP_ANIMATION_MS = 175;

export default function useLineItemReorder({
  rows,
  setRows,
  setSearchQueries,
  closeSearch,
  disabled = false,
  idPrefix,
  darkMode = false,
}) {
  const dragRef = useRef(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [targetIndex, setTargetIndex] = useState(null);

  const moveRow = (from, to) => {
    if (disabled || from === to || to < 0 || to >= rows.length) return;
    setRows((current) => {
      const reordered = [...current];
      const [moved] = reordered.splice(from, 1);
      reordered.splice(to, 0, moved);
      return reordered;
    });
    setSearchQueries((queries) => {
      const indices = rows.map((_, index) => index);
      const [moved] = indices.splice(from, 1);
      indices.splice(to, 0, moved);
      return Object.fromEntries(
        indices.map((oldIndex, newIndex) => [newIndex, queries[oldIndex]])
      );
    });
    closeSearch();
    window.requestAnimationFrame(() =>
      document
        .getElementById(`${idPrefix}-item-move-${to}`)
        ?.focus({ preventScroll: true })
    );
  };

  const resetDrag = () => {
    const drag = dragRef.current;
    if (drag?.dropTimer) window.clearTimeout(drag.dropTimer);
    drag?.list?.classList.remove("line-item-list-is-reordering");
    drag?.layer?.remove();
    dragRef.current = null;
    setDraggedIndex(null);
    setTargetIndex(null);
  };

  useEffect(
    () => () => {
      const drag = dragRef.current;
      if (drag?.dropTimer) window.clearTimeout(drag.dropTimer);
      drag?.list?.classList.remove("line-item-list-is-reordering");
      drag?.layer?.remove();
    },
    []
  );

  const beginDrag = (event, index) => {
    if (disabled || rows.length < 2 || event.button !== 0) return;
    const source = event.currentTarget.closest("[data-line-item-index]");
    const list = event.currentTarget.closest("[data-line-item-list]");
    if (!source || !list) return;

    const sourceBounds = source.getBoundingClientRect();
    const layer = document.createElement("div");
    layer.className = `line-item-drag-layer invoice-flow-shell${
      darkMode ? " dark" : ""
    }`;
    layer.dataset.theme = darkMode ? "dark" : "light";
    layer.setAttribute("aria-hidden", "true");
    const surface = document.createElement("div");
    surface.className = "invoice-flow-card line-item-drag-surface";
    const preview = source.cloneNode(true);
    preview.classList.add("line-item-drag-preview", "line-item-is-dragging");
    preview.classList.remove(
      "line-item-drop-before",
      "line-item-drop-after",
      "line-item-is-shifting"
    );
    preview.removeAttribute("data-line-item-index");
    preview.removeAttribute("style");
    preview
      .querySelectorAll(
        ".quotation-item-search-dropdown, .quotation-figma-dropdown-menu"
      )
      .forEach((element) => element.remove());
    preview
      .querySelectorAll("[id]")
      .forEach((element) => element.removeAttribute("id"));
    preview
      .querySelectorAll("input, button, select, textarea, [tabindex]")
      .forEach((element) => element.setAttribute("tabindex", "-1"));
    preview.style.left = `${sourceBounds.left}px`;
    preview.style.top = `${sourceBounds.top}px`;
    preview.style.width = `${sourceBounds.width}px`;
    preview.style.height = `${sourceBounds.height}px`;
    surface.appendChild(preview);
    layer.appendChild(surface);
    document.body.appendChild(layer);
    list.classList.add("line-item-list-is-reordering");

    dragRef.current = {
      from: index,
      to: index,
      pointerID: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      sourceBounds,
      rowHeight: sourceBounds.height,
      list,
      layer,
      preview,
      active: false,
      dropping: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    closeSearch();
    setDraggedIndex(index);
    setTargetIndex(index);
  };

  const moveDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerID !== event.pointerId) return;
    if (
      !drag.active &&
      Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 5
    )
      return;
    drag.active = true;
    drag.preview?.style.setProperty(
      "--line-item-drag-offset-x",
      `${event.clientX - drag.startX}px`
    );
    drag.preview?.style.setProperty(
      "--line-item-drag-offset-y",
      `${event.clientY - drag.startY}px`
    );

    const candidates = Array.from(
      drag.list.querySelectorAll("[data-line-item-index]")
    );
    if (candidates.length) {
      const previewCenterY =
        drag.sourceBounds.top +
        (event.clientY - drag.startY) +
        drag.rowHeight / 2;
      const target = candidates.reduce(
        (nearest, item) => {
          const itemIndex = Number(item.dataset.lineItemIndex);
          const currentShift =
            drag.from < drag.to && itemIndex > drag.from && itemIndex <= drag.to
              ? -drag.rowHeight
              : drag.from > drag.to &&
                itemIndex >= drag.to &&
                itemIndex < drag.from
              ? drag.rowHeight
              : 0;
          const bounds = item.getBoundingClientRect();
          const centerY = bounds.top - currentShift + bounds.height / 2;
          const distance = Math.abs(previewCenterY - centerY);
          return distance < nearest.distance ? { item, distance } : nearest;
        },
        { item: candidates[0], distance: Number.POSITIVE_INFINITY }
      ).item;
      const nextTargetIndex = Number(target.dataset.lineItemIndex);
      if (nextTargetIndex !== drag.to) {
        drag.to = nextTargetIndex;
        setTargetIndex(nextTargetIndex);
      }
    }

    const listBounds = drag.list.getBoundingClientRect();
    if (drag.list.scrollHeight > drag.list.clientHeight) {
      const edgeSize = Math.min(64, listBounds.height / 3);
      if (event.clientY < listBounds.top + edgeSize) drag.list.scrollTop -= 18;
      else if (event.clientY > listBounds.bottom - edgeSize)
        drag.list.scrollTop += 18;
    }
    if (event.clientY < 64) window.scrollBy(0, -20);
    else if (event.clientY > window.innerHeight - 64) window.scrollBy(0, 20);
  };

  const endDrag = (event) => {
    const drag = dragRef.current;
    const shouldAnimateDrop = Boolean(
      drag &&
        drag.pointerID === event.pointerId &&
        drag.active &&
        drag.from !== drag.to
    );
    if (shouldAnimateDrop) drag.dropping = true;
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    if (!shouldAnimateDrop) {
      resetDrag();
      return;
    }

    const target = drag.list.querySelector(
      `[data-line-item-index="${drag.to}"]`
    );
    if (!target || !drag.preview) {
      const { from, to } = drag;
      resetDrag();
      moveRow(from, to);
      return;
    }

    const targetBounds = target.getBoundingClientRect();
    const targetShift = drag.to > drag.from ? -drag.rowHeight : drag.rowHeight;
    const targetTop = targetBounds.top - targetShift;
    const destinationTop =
      drag.to > drag.from
        ? targetTop + target.offsetHeight - drag.rowHeight
        : targetTop;
    drag.preview.style.transition =
      "transform 170ms cubic-bezier(0.2, 0, 0, 1), box-shadow 170ms ease";
    drag.preview.getBoundingClientRect();
    drag.preview.style.setProperty("--line-item-drag-offset-x", "0px");
    drag.preview.style.setProperty(
      "--line-item-drag-offset-y",
      `${destinationTop - drag.sourceBounds.top}px`
    );
    drag.dropTimer = window.setTimeout(() => {
      const { from, to } = drag;
      resetDrag();
      moveRow(from, to);
    }, DROP_ANIMATION_MS);
  };

  const getHandleProps = (index) => ({
    id: `${idPrefix}-item-move-${index}`,
    type: "button",
    className: "line-item-drag-handle",
    disabled: disabled || rows.length < 2,
    "aria-label": `Move ${
      rows[index]?.itemName || `line ${index + 1}`
    }. Use Up or Down arrow keys to reorder.`,
    "aria-pressed": draggedIndex === index,
    title: "Drag to reorder, or use Up and Down arrow keys",
    onPointerDown: (event) => beginDrag(event, index),
    onPointerMove: moveDrag,
    onPointerUp: endDrag,
    onPointerCancel: resetDrag,
    onLostPointerCapture: () => {
      if (!dragRef.current?.dropping) resetDrag();
    },
    onKeyDown: (event) => {
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      event.preventDefault();
      moveRow(index, index + (event.key === "ArrowUp" ? -1 : 1));
    },
  });

  const getRowProps = (index, className) => {
    const isDragged = draggedIndex === index;
    const shiftsUp =
      draggedIndex !== null &&
      targetIndex !== null &&
      draggedIndex < targetIndex &&
      index > draggedIndex &&
      index <= targetIndex;
    const shiftsDown =
      draggedIndex !== null &&
      targetIndex !== null &&
      draggedIndex > targetIndex &&
      index >= targetIndex &&
      index < draggedIndex;
    const rowHeight = dragRef.current?.rowHeight || 0;
    return {
      "data-line-item-index": index,
      className: `${className}${isDragged ? " line-item-is-dragging" : ""}${
        shiftsUp || shiftsDown ? " line-item-is-shifting" : ""
      }${
        targetIndex === index && !isDragged
          ? draggedIndex > index
            ? " line-item-drop-before"
            : " line-item-drop-after"
          : ""
      }`,
      style: shiftsUp
        ? { "--line-item-row-shift-y": `${-rowHeight}px` }
        : shiftsDown
        ? { "--line-item-row-shift-y": `${rowHeight}px` }
        : undefined,
    };
  };

  return { getHandleProps, getRowProps };
}
